import "./ResourceShowcasePage.scss";
import CheckmarkIcon from "../icons/content-pages/CheckmarkIcon";
import { useEffect } from "react";
import { ApartmentOutlined, ApiOutlined, CheckCircleOutlined, DeploymentUnitOutlined, FallOutlined, PartitionOutlined, TeamOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { useRecoilValue } from "recoil";
import { memberState } from "../../state/atoms";

export default function ResourceShowcasePage() {
  const member = useRecoilValue(memberState);

  useEffect(() => {
    let meta = document.createElement('meta');
    meta.name = "robots";
    meta.content = "noindex";
    document.getElementsByTagName('head')[0].prepend(meta);
  }, [])
  
  return (
  <main className="resource-showcase-content">
    <section className={"one"}>
      <div className="content">
        <div className={"flex"}>
          <div className={"half text"}>
            <h1>Who is MustHaveMenus?</h1>
            <p>
              MustHaveMenus is the menu operations platform for restaurants. Update your menu once, in Toast or in MustHaveMenus, and it publishes everywhere: digital menu boards, print, your website, QR menus, and listings on Google, Apple, Yelp and more.
            </p>
            <p>
              Brands keep central control of design and data. Local teams make fast, on-brand updates without waiting on marketing.
            </p>
            <div className="desktop-only">
              <a href={member.id ? "/feature/book-demo-auth.html" : "/feature/book-demo-hello.html"} target={"_blank"} rel={"noreferrer"} className={"ant-btn ant-btn-primary ant-btn-lg"}>Book an Intro</a>
            </div>
          </div>
          <div className={"half video"}>
            <iframe width="550" height="315" src="https://www.youtube.com/embed/fwOgqRKdJVQ?si=axDawOHywbDTg0_d" title="YouTube video player"  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen></iframe>
          </div>
          <a href={member.id ? "/feature/book-demo-auth.html" : "/feature/book-demo-hello.html"} target={"_blank"} rel={"noreferrer"} className={"ant-btn ant-btn-primary ant-btn-lg mobile-only"}>Book an Intro</a>
        </div>
      </div>
    </section>

    <section className={"two"}>
      <div className="content">
        <h2>Benefits</h2>
        <div className={"flex"}>
          <div className={"half"}>
            <ul>
              <li>
                <ThunderboltOutlined/>
                <div>
                  <p>Menu updates 70% faster, everywhere.</p>
                  <p>Change an item or price once and it flows to every screen, print file, web menu and listing. One customer saves 30+ hours a week.</p>
                </div>
              </li>
              <li>
                <FallOutlined/>
                <div>
                  <p>Lower design costs.</p>
                  <p>Brand-approved designs let local teams handle their own updates, clearing the marketing and agency backlog.</p>
                </div>
              </li>
              <li>
                <ApartmentOutlined/>
                <div>
                  <p>One platform, not four vendors.</p>
                  <p>Print, digital signage, website menus and listings live in one place, with one login and one bill.</p>
                </div>
              </li>
            </ul>
          </div>
          <div className={"half"}>
            <ul>
              <li>
                <div><img src="https://timber.mhmcdn.com/site/marketing/icons/BrandStar.svg" alt="No brand drift"/></div>
                <div>
                  <p>No more brand drift.</p>
                  <p>Operators work in data-only mode: they edit items and prices, and the design stays locked.</p>
                </div>
              </li>
              <li>
                <DeploymentUnitOutlined/>
                <div>
                  <p>Central visibility.</p>
                  <p>See every brand, location, menu and screen from a single dashboard.</p>
                </div>
              </li>
              <li>
                <CheckCircleOutlined/>
                <div>
                  <p>Fewer data errors.</p>
                  <p>Menu data syncs from your POS, so prices and items match on every channel.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className={"three"}>
      <div className="content">
        <h2>Menu Management &amp; Design</h2>
        <p className={"intro"}>Digital menu boards that update themselves. Display turns the same menu data behind your print and web menus into screens, so a price change in Toast reaches the board automatically.</p>
        <div className={"flex reverse"}>
          <div className={"half img"}>
            <img src={"https://timber.mhmcdn.com/site/content/resource-showcase/MenuManagement.webp"} alt="Menu Management &amp; Design"/>
          </div>
          <div className={"half text"}>
            <ul>
              <li>
                <CheckmarkIcon/>
                <div><b>Brand setup.</b> Brand kits, fonts and locked brand templates keep every location on-standard.</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div><b>Teams and locations.</b> Assign people to the brands, folders and locations they manage, mirroring how your organization works.</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div><b>POS integration.</b> Pull items, prices and descriptions straight from Toast or Square, and keep them in sync.</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div><b>Menu Services.</b> Our design team builds your brand-approved templates from your data, so you launch ready to go.</div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className={"four"}>
      <div className="content">
        <h2>Display</h2>
        <p className={"intro"}>Digital menu boards that update themselves. Display turns the same menu data behind your print and web menus into screens, so a price change in Toast reaches the board automatically.</p>
        <div className={"flex"}>
          <div className={"half img"}>
            <img src={"https://timber.mhmcdn.com/site/content/resource-showcase/Display.webp"} alt="Display"/>
          </div>
          <div className={"half text"}>
            <ul>
              <li>
                <CheckmarkIcon/>
                <div><b>Manage 1 to 500+ screens</b> from one dashboard, with location-level access for local teams.</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div><b>Automatic updates from your POS,</b> no re-exporting or USB sticks.</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div><b>Playlists and scheduling</b> for dayparts, days of the week and limited-time offers.</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div><b>Plug-and-play hardware</b> that's up and running in minutes. Connect over Wi-Fi or Ethernet, plug in via HDMI, and show your menu in crisp 4K. Bluetooth 4.0 included.</div>
              </li>
            </ul>
          </div>
        </div>
        <div className={"button-holder"}>
          <a href={"/feature/digital-menu-board-solution.html"} className={"ant-btn ant-btn-primary ant-btn-lg"}>See Display</a>
        </div>
      </div>
    </section>

    <section className={"five"}>
      <div className="content">
        <h2>Listings</h2>
        <p className={"intro"}>Manage listings right where you manage your menu. Publish menu updates directly to Google Business Profile, Apple, Bing, Facebook and dozens more publishers, with no middleman.</p>
        <div className={"flex reverse"}>
          <div className={"half img"}>
            <img src={"https://timber.mhmcdn.com/site/content/resource-showcase/Listings.webp"} alt="Listings"/>
          </div>
          <div className={"half text"}>
            <ul>
              <li>
                <CheckmarkIcon/>
                <div><b>Searchable menus</b> on Google and other publishers, updated with every menu change.</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div><b>Hours, holidays, links, attributes and photos</b> managed for every location in one place.</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div><b>Better local search and guest experience,</b> because what diners see online matches what's in the store.</div>
              </li>
            </ul>
          </div>
        </div>
        <div className={"button-holder"}>
          <a href={"/feature/restaurant-listings-management.html"} className={"ant-btn ant-btn-primary ant-btn-lg"}>See Listings</a>
        </div>
      </div>
    </section>

    <section className={"six"}>
      <div className="content">
        <h2>Online Menus, QR Codes, Link Pages &amp; Print</h2>
        <p className={"intro"}>Every menu, on every surface, from one update. The same menu data behind your screens and listings also powers your online menus, QR codes and print, so guests see the same items and prices at the table, on their phone and on the takeout bag.</p>
        <div className={"flex"}>
          <div className={"half img"}>
            <img src={"https://timber.mhmcdn.com/site/content/resource-showcase/CustomerJourneyOct26.webp"} alt="Online Menus, QR Codes, Link Pages &amp; Print"/>
          </div>
          <div className={"half text"}>
            <ul>
              <li>
                <CheckmarkIcon/>
                <div><b>Web and QR.</b> Mobile-friendly online menus, QR codes and Link Pages run on the same menu data, so they're never out of date. Link them from your website, social profiles and review sites.</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div><b>Print.</b> Order restaurant-quality printing without leaving the app, from the same file your team just updated. Restaurant-proof papers, lamination, and next-day shipping are all one click away.</div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className={"seven"}>
      <div className="content">
        <h2>The All-in-One Platform</h2>
        <p className={"intro"}>Most restaurant groups run menus through four or five vendors that don't talk to each other. MustHaveMenus replaces them with one platform connected to your POS.</p>
        <div className={"flex"}>
          <div className={"half"}>
            <ul>
              <li>
                <div><img src="https://timber.mhmcdn.com/site/marketing/icons/DesignSets.svg" alt="Design Sets"/></div>
                <div>
                  <p>World's Largest Menu Collection</p>
                  <p>Over 25,000 customizable templates and counting.</p>
                </div>
              </li>
              <li>
                <div><img src="https://timber.mhmcdn.com/site/marketing/icons/Shop.svg" alt="Megaphone"/></div>
                <div>
                  <p>One-stop Shop</p>
                  <p>All your restaurant marketing materials like flyers, posters, business cards, and more in one convenient location.</p>
                </div>
              </li>
              <li>
                <div><img src="https://timber.mhmcdn.com/site/marketing/icons/QRCode.svg" alt="QR Code"/></div>
                <div>
                  <p>Digital Tools</p>
                  <p>Share your designs with the world using our dynamic QR codes and sharelinks.</p>
                </div>
              </li>
              <li>
                <div><img src="https://timber.mhmcdn.com/site/marketing/icons/UtensilsForkKnife.svg" alt="Mobile Menus"/></div>
                <div>
                  <p>Built for Restaurants</p>
                  <p>Every tool, print or digital, is made for how menus actually change.</p>
                </div>
              </li>
              <li>
                <div><img src="https://timber.mhmcdn.com/site/marketing/icons/CustomerService.svg" alt="CS"/></div>
                <div>
                  <p>Expert Customer Support</p>
                  <p>Friendly customer service experts are ready to help resolve any issue, usually in a matter of minutes.</p>
                </div>
              </li>
            </ul>
          </div>
          <div className={"half"}>
            <ul>
              <li>
                <div><img src="https://timber.mhmcdn.com/site/marketing/icons/PrinterSimple.svg" alt="Printer"/></div>
                <div>
                  <p>The Print Pros</p>
                  <p>Printing done right for restaurants. Covers a variety of products like sandwich boards, folded takeout menus, and business cards. Plus you have the option for high-quality finishes and laminates.</p>
                </div>
              </li>
              <li>
                <div><img src="https://timber.mhmcdn.com/site/marketing/icons/NodeIndex.svg" alt="Printer"/></div>
                <div>
                  <p>POS Integrations</p>
                  <p>Seamlessly pull data from POS providers into your designs. Keep all your data synced with our Design Updater.</p>
                </div>
              </li>
              <li>
                <div><img src="https://timber.mhmcdn.com/site/marketing/icons/DigiBoard.svg" alt="DMBs"/></div>
                <div>
                  <p>Digital Menu Boards</p>
                  <p>Create digital menus and signage for various screen sizes. Simple to design, display and update.</p>
                </div>
              </li>
              <li>
                <div><img src="https://timber.mhmcdn.com/site/marketing/icons/Hospitality.svg" alt="Menu Services"/></div>
                <div>
                  <p>Menu Services</p>
                  <p>Menu Services can create professional menu designs using your data starting at $249.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className={"eight"}>
      <div className="content">
        <h2>Customer Journey</h2>
        <p className={"intro"}>From first call to every channel live, in five steps.</p>
        <div className={"flex"}>
          <div className={"item"}>
            <div>
              <ApiOutlined/>
            </div>
            <div>
              <h3>Connect</h3>
              <p>Link your Toast or Square POS so menu data flows in automatically.</p>
            </div>
          </div>
          <div className={"item"}>
            <div>
              <img src="https://timber.mhmcdn.com/site/marketing/icons/LogoRed.svg" alt="Set up your brand"/>
            </div>
            <div>
              <h3>Set up your brand</h3>
              <p>Our Menu Services team builds brand-approved templates for print, screens and web.</p>
            </div>
          </div>
          <div className={"item"}>
            <div>
              <TeamOutlined/>
            </div>
            <div>
              <h3>Add teams and locations</h3>
              <p>Give each manager access to just the locations and menus they run.</p>
            </div>
          </div>
          <div className={"item"}>
            <div>
              <img src="https://timber.mhmcdn.com/site/marketing/icons/MenuDataRed.svg" alt="Update in data-only mode"/>
            </div>
            <div>
              <h3>Update in data-only mode</h3>
              <p>Operators change items, prices and specials; the design stays locked..</p>
            </div>
          </div>
          <div className={"item"}>
            <div>
              <PartitionOutlined/>
            </div>
            <div>
              <h3>Publish everywhere</h3>
              <p>One click updates screens, print files, online menus, QR codes and listings.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className={"nine"}>
      <div className="content">
        <div className={"flex"}>
          <div className={"half"}>
            <img src={"https://timber.mhmcdn.com/site/content/resource-showcase/CustomerCare.webp"} alt="Customer Care"/>
          </div>
          <div className={"half"}>
            <h2>Customer Care</h2>
            <p>Real people, at every stage. Our onboarding team gets your brand, data and screens set up, and our support team answers most questions in minutes. Enterprise customers get a dedicated account contact.</p>
          </div>
        </div>
      </div>
    </section>

    <section className={"ten"}>
      <div className="content">
        <h2>Features</h2>
        <div className={"flex"}>
          <div className={"column"}>
            <h3>Design &amp; Print</h3>
            <ul>
              <li>
                <CheckmarkIcon/>
                <div>Menu designer built for restaurants</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div>Brand kits, custom fonts and locked brand templates</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div>Data-only editing for operators</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div>One-click resizing across formats</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div>Built-in stock image and video library</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div>Order restaurant-proof prints in the app</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div>Next-day shipping and satisfaction guarantee</div>
              </li>
            </ul>
          </div>
          <div className={"column"}>
            <h3>Menu Data &amp; Control</h3>
            <ul>
              <li>
                <CheckmarkIcon/>
                <div>Toast and Square POS integration</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div>Automatic sync of items, prices and descriptions</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div>Item library across brands and locations</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div>Teams, permissions and approval workflows</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div>Central dashboard for every location</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div>AI Spell Check</div>
              </li>
            </ul>
          </div>
          <div className={"column"}>
            <h3>Screens, Listings &amp; Web</h3>
            <ul>
              <li>
                <CheckmarkIcon/>
                <div>Digital signage for up to 500 screens</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div>Playlists, scheduling and 4K video</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div>Listings on 60+ publishers, including Google and Yelp</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div>Hours, holidays, attributes and photos</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div>Mobile menus, QR codes and Link Pages</div>
              </li>
              <li>
                <CheckmarkIcon/>
                <div>Web embed codes and search analytics</div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className={"eleven"}>
      <div className="content">
        <h2>Video Resources</h2>
        <div className={"flex"}>
          <div>
            <iframe width="325" height="200" src="https://www.youtube.com/embed/eJdpy_2DjI0" title="YouTube video player"  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen></iframe>
            <p>MustHaveMenus Overview</p>
          </div>
          <div>
            <iframe width="325" height="200" src="https://www.youtube.com/embed/JW8DJ3sYQb8?si=5JW4FbIP2FjnumA9" title="YouTube video player"  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen></iframe>
            <p>The Menu Maker</p>
          </div>
        </div>
      </div>
    </section>
  </main>)
}
